import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Brain, Upload, FileText, Loader2, Clock, AlertTriangle,
  Shield, Scale, Sparkles, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AnalysisResult({ result }) {
  if (!result) return null;
  return (
    <div className="max-w-none" data-testid="analysis-result">
      <Markdown content={result} />
    </div>
  );
}

function AnalysisHistoryItem({ analysis, onClick }) {
  const typeLabels = {
    quick: "Analyse rapide",
    deep: "Analyse approfondie",
    compare: "Comparaison",
    strategy: "Stratégie juridique"
  };
  const typeColors = {
    quick: "bg-blue-100 text-blue-700",
    deep: "bg-purple-100 text-purple-700",
    compare: "bg-amber-100 text-amber-700",
    strategy: "bg-emerald-100 text-emerald-700"
  };

  return (
    <div
      className="p-3 rounded-lg border border-slate-200 hover:border-blue-200 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={"analysis-" + analysis.analysis_id}
    >
      <div className="flex items-center justify-between mb-1">
        <Badge className={typeColors[analysis.analysis_type] || "bg-slate-100 text-slate-700"}>
          {typeLabels[analysis.analysis_type] || analysis.analysis_type}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {new Date(analysis.created_at).toLocaleDateString("fr-FR")}
        </span>
      </div>
      <p className="text-xs text-muted-foreground truncate">{analysis.model_used}</p>
    </div>
  );
}

function CompareSide({ index, docId, setDocId, text, setText, file, setFile, documents }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Document {index}</CardTitle>
        <CardDescription>Uploadez un fichier (.docx/.txt), choisissez un document, ou collez le texte</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="block">
          <input
            type="file" className="hidden" accept=".docx,.doc,.txt"
            data-testid={"compare-file-" + index}
            onChange={function(e) { const f = e.target.files && e.target.files[0]; if (f) { setFile(f); setDocId(""); setText(""); } }}
          />
          <Button asChild variant="outline" className="w-full justify-start overflow-hidden">
            <span className="flex items-center w-full min-w-0">
              <Upload className="w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">{file ? file.name : "Uploader un fichier"}</span>
            </span>
          </Button>
        </label>
        {documents.length > 0 && (
          <Select value={docId} onValueChange={function(v) { setDocId(v); setFile(null); setText(""); }}>
            <SelectTrigger data-testid={"compare-doc-" + index}>
              <SelectValue placeholder="Ou sélectionner un document..." />
            </SelectTrigger>
            <SelectContent>
              {documents.map(function(d) {
                return <SelectItem key={d.document_id} value={d.document_id}>{d.original_filename}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        )}
        <Textarea
          value={text}
          onChange={function(e) { setText(e.target.value); setFile(null); setDocId(""); }}
          placeholder="Ou collez le texte ici..."
          className="min-h-[120px]"
          data-testid={"compare-text-" + index}
        />
      </CardContent>
    </Card>
  );
}

export default function AIAnalysis({ user, setUser }) {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState("");
  const [textInput, setTextInput] = useState("");
  const [analysisType, setAnalysisType] = useState("quick");
  const [instructions, setInstructions] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [activeTab, setActiveTab] = useState("new");
  // Comparison state (two sides)
  const [cmpDoc1, setCmpDoc1] = useState("");
  const [cmpDoc2, setCmpDoc2] = useState("");
  const [cmpText1, setCmpText1] = useState("");
  const [cmpText2, setCmpText2] = useState("");
  const [cmpFile1, setCmpFile1] = useState(null);
  const [cmpFile2, setCmpFile2] = useState(null);

  useEffect(function() {
    fetchAnalyses();
    fetchDocuments();
  }, []);

  async function fetchAnalyses() {
    try {
      const res = await fetch(API_URL + "/api/ai/analyses", { credentials: "include" });
      if (res.ok) setAnalyses(await res.json());
    } catch (e) { /* ignore */ }
  }

  async function fetchDocuments() {
    try {
      const res = await fetch(API_URL + "/api/documents", { credentials: "include" });
      if (res.ok) setDocuments(await res.json());
    } catch (e) { /* ignore */ }
  }

  async function handleAnalyze() {
    if (!textInput.trim() && !selectedDoc) {
      toast.error("Veuillez fournir du texte ou sélectionner un document");
      return;
    }
    setAnalyzing(true);
    setCurrentResult(null);

    try {
      var url, body, headers;
      
      if (analysisType === "risk_scan") {
        url = API_URL + "/api/ai/risk-scan";
        var fd = new FormData();
        fd.append("text", textInput);
        if (selectedDoc) fd.append("document_id", selectedDoc);
        var res = await fetch(url, { method: "POST", credentials: "include", body: fd });
        if (res.ok) { var data = await res.json(); setCurrentResult(data); fetchAnalyses(); toast.success("Analyse terminée"); }
        else { var err = await res.json(); toast.error(err.detail || "Erreur"); }
      } else if (analysisType === "litigation") {
        url = API_URL + "/api/ai/predict-litigation";
        var fd2 = new FormData();
        fd2.append("text", textInput);
        var res2 = await fetch(url, { method: "POST", credentials: "include", body: fd2 });
        if (res2.ok) { var data2 = await res2.json(); setCurrentResult(data2); fetchAnalyses(); toast.success("Prédiction terminée"); }
        else { var err2 = await res2.json(); toast.error(err2.detail || "Erreur"); }
      } else if (analysisType === "negotiate") {
        url = API_URL + "/api/ai/negotiate";
        var fd3 = new FormData();
        fd3.append("text", textInput);
        fd3.append("party", "equilibre");
        var res3 = await fetch(url, { method: "POST", credentials: "include", body: fd3 });
        if (res3.ok) { var data3 = await res3.json(); setCurrentResult(data3); fetchAnalyses(); toast.success("Négociation terminée"); }
        else { var err3 = await res3.json(); toast.error(err3.detail || "Erreur"); }
      } else {
        var res4 = await fetch(API_URL + "/api/ai/analyze", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            text: textInput || null,
            document_id: selectedDoc || null,
            analysis_type: analysisType,
            instructions: instructions || null
          })
        });
        if (res4.ok) { var data4 = await res4.json(); setCurrentResult(data4); fetchAnalyses(); toast.success("Analyse terminée"); }
        else { var err4 = await res4.json(); toast.error(err4.detail || "Erreur d'analyse"); }
      }
    } catch (e) {
      toast.error("Erreur de connexion");
    }
    setAnalyzing(false);
  }

  async function handleCompare() {
    const has1 = cmpFile1 || cmpDoc1 || cmpText1.trim();
    const has2 = cmpFile2 || cmpDoc2 || cmpText2.trim();
    if (!has1 || !has2) {
      toast.error("Fournissez les deux documents à comparer");
      return;
    }
    setAnalyzing(true);
    setCurrentResult(null);
    const fd = new FormData();
    if (cmpFile1) fd.append("file1", cmpFile1);
    else if (cmpDoc1) fd.append("document_id_1", cmpDoc1);
    else fd.append("text1", cmpText1);
    if (cmpFile2) fd.append("file2", cmpFile2);
    else if (cmpDoc2) fd.append("document_id_2", cmpDoc2);
    else fd.append("text2", cmpText2);
    try {
      const res = await fetch(API_URL + "/api/ai/compare", { method: "POST", credentials: "include", body: fd });
      if (res.ok) { setCurrentResult(await res.json()); fetchAnalyses(); toast.success("Comparaison terminée"); }
      else { const e = await res.json(); toast.error(e.detail || "Erreur de comparaison"); }
    } catch (e) { toast.error("Erreur de connexion"); }
    setAnalyzing(false);
  }

  async function loadAnalysis(analysisId) {
    try {
      const res = await fetch(API_URL + "/api/ai/analyses/" + analysisId, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCurrentResult(data);
        setActiveTab("new");
      }
    } catch (e) { toast.error("Erreur"); }
  }

  const analysisTypes = [
    { value: "quick", label: "Analyse rapide", desc: "Résumé, risques, recommandations", icon: Sparkles, model: "Claude Sonnet 4.5" },
    { value: "deep", label: "Analyse approfondie", desc: "Clause par clause, score de risque, conformité", icon: Shield, model: "Claude Opus 4.5" },
    { value: "compare", label: "Comparaison", desc: "Comparer deux documents", icon: Scale, model: "Claude Opus 4.5" },
    { value: "strategy", label: "Stratégie juridique", desc: "Analyse de litige, recommandations", icon: AlertTriangle, model: "Claude Opus 4.5" },
    { value: "risk_scan", label: "Scanner de risques", desc: "Scoring de risque détaillé avec JSON", icon: Shield, model: "Claude Opus 4.5" },
    { value: "litigation", label: "Prédiction litige", desc: "Prédire l'issue d'un litige", icon: Scale, model: "Claude Opus 4.5" },
    { value: "negotiate", label: "Négociation IA", desc: "Équilibrage automatique des clauses", icon: AlertTriangle, model: "Claude Opus 4.5" },
  ];

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="ai-analysis-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">
            Analyse IA
          </h1>
          <p className="text-sm text-muted-foreground">
            Analysez vos documents juridiques avec Claude (Sonnet & Opus)
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Input */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="new">Nouvelle analyse</TabsTrigger>
                <TabsTrigger value="history">Historique</TabsTrigger>
              </TabsList>

              <TabsContent value="new" className="space-y-6 mt-4">
                {/* Analysis type selection */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
                  {analysisTypes.map(function(t) {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.value}
                        className={"flex flex-col items-start p-3 rounded-lg border transition-colors text-left " +
                          (analysisType === t.value
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-blue-200")
                        }
                        onClick={function() { setAnalysisType(t.value); }}
                        data-testid={"type-" + t.value}
                      >
                        <Icon className={"w-5 h-5 mb-2 " + (analysisType === t.value ? "text-blue-600" : "text-slate-400")} />
                        <span className="text-sm font-medium">{t.label}</span>
                        <span className="text-xs text-muted-foreground">{t.model}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Comparison mode: two documents */}
                {analysisType === "compare" && (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <CompareSide index={1} docId={cmpDoc1} setDocId={setCmpDoc1} text={cmpText1} setText={setCmpText1} file={cmpFile1} setFile={setCmpFile1} documents={documents} />
                      <CompareSide index={2} docId={cmpDoc2} setDocId={setCmpDoc2} text={cmpText2} setText={setCmpText2} file={cmpFile2} setFile={setCmpFile2} documents={documents} />
                    </div>
                    <Button onClick={handleCompare} disabled={analyzing} className="w-full" data-testid="compare-btn">
                      {analyzing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Comparaison en cours...</>) : (<><Scale className="w-4 h-4 mr-2" />Comparer les documents</>)}
                    </Button>
                  </div>
                )}

                {/* Document or text input (single-input modes) */}
                {analysisType !== "compare" && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Document à analyser</CardTitle>
                    <CardDescription>Collez le texte ou sélectionnez un document uploadé</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {documents.length > 0 && (
                      <Select value={selectedDoc} onValueChange={function(v) { setSelectedDoc(v); setTextInput(""); }}>
                        <SelectTrigger data-testid="select-document">
                          <SelectValue placeholder="Sélectionner un document..." />
                        </SelectTrigger>
                        <SelectContent>
                          {documents.map(function(d) {
                            return (
                              <SelectItem key={d.document_id} value={d.document_id}>
                                {d.original_filename}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                    <Textarea
                      value={textInput}
                      onChange={function(e) { setTextInput(e.target.value); setSelectedDoc(""); }}
                      placeholder="Collez ici le texte du document juridique à analyser..."
                      className="min-h-[200px]"
                      data-testid="analysis-text-input"
                    />
                    <Textarea
                      value={instructions}
                      onChange={function(e) { setInstructions(e.target.value); }}
                      placeholder="Instructions spécifiques (optionnel)..."
                      className="min-h-[60px]"
                      data-testid="analysis-instructions"
                    />
                    <Button
                      onClick={handleAnalyze}
                      disabled={analyzing || (!textInput.trim() && !selectedDoc)}
                      className="w-full"
                      data-testid="analyze-btn"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyse en cours...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-2" />
                          Lancer l'analyse
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
                )}

                {/* Result display */}
                {currentResult && (
                  <Card data-testid="analysis-result-card">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Résultat de l'analyse</CardTitle>
                        <Badge variant="secondary">{currentResult.model_used}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <AnalysisResult result={currentResult.result} />
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                {analyses.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">Aucune analyse effectuée</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {analyses.map(function(a) {
                      return (
                        <AnalysisHistoryItem
                          key={a.analysis_id}
                          analysis={a}
                          onClick={function() { loadAnalysis(a.analysis_id); }}
                        />
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Brain className="w-5 h-5 text-blue-600" />
                  Modèles IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-sm font-medium text-blue-800">Claude Sonnet 4.5</p>
                  <p className="text-xs text-blue-600 mt-1">Analyse rapide, interaction, rapports simples</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
                  <p className="text-sm font-medium text-purple-800">Claude Opus 4.5</p>
                  <p className="text-xs text-purple-600 mt-1">Analyse complexe, litiges, scoring de risque</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Statistiques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Analyses effectuées</span>
                  <span className="font-medium">{analyses.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Documents disponibles</span>
                  <span className="font-medium">{documents.length}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
